import { useEffect } from 'react';
import { ShoppingCart, RefreshCw, Trash2 } from 'lucide-react';
import { useUserCart, CartItem } from './useUserCart';
import { ProxyImage } from '../../../lib/imageProxy';

interface UserCartPreviewProps {
  userId: string;
  userPriceType: string;
  onClearCart?: () => void;
}

export default function UserCartPreview({
  userId,
  userPriceType,
  onClearCart
}: UserCartPreviewProps) {
  const { cart, loading, error, fetchCart, clearCart } = useUserCart(userId);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleClearCart = async () => {
    if (!confirm('¿Vaciar el carrito de este usuario? Esta accion no se puede deshacer.')) {
      return;
    }

    const success = await clearCart();
    if (success) {
      onClearCart?.();
    }
  };

  const getItemPrice = (item: CartItem): number => {
    const priceData = item.prices.find(p => p.price_type === userPriceType);
    return priceData?.offer_price ?? priceData?.price ?? item.product?.base_price ?? 0;
  };

  const hasOffer = (item: CartItem): boolean => {
    const priceData = item.prices.find(p => p.price_type === userPriceType);
    return priceData?.offer_price != null && priceData.offer_price < (priceData?.price ?? 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-500">Cargando carrito...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 rounded-lg text-red-600 text-sm">
        Error: {error}
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center py-8 text-gray-400">
        <ShoppingCart className="w-12 h-12 mb-2" />
        <p>El carrito esta vacio</p>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-GT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Ultima actualizacion: {formatDate(cart.updated_at)}
        </div>
        <button
          onClick={handleClearCart}
          className="flex items-center gap-2 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Vaciar carrito
        </button>
      </div>

      {/* Items */}
      <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
        {cart.items.map((item) => {
          const price = getItemPrice(item);
          const subtotal = price * item.quantity;
          const isOffer = hasOffer(item);

          return (
            <div key={item.id} className="flex items-center gap-4 p-3 hover:bg-gray-50">
              <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                {item.product?.main_image ? (
                  <ProxyImage
                    src={item.product.main_image}
                    alt={item.product?.name || 'Producto'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <ShoppingCart className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {item.product?.name || 'Producto sin nombre'}
                </p>
                <p className="text-xs text-gray-500">
                  Cantidad: {item.quantity}
                </p>
              </div>
              <div className="text-right">
                <p className={`text-sm font-semibold ${isOffer ? 'text-red-600' : 'text-gray-900'}`}>
                  Q{subtotal.toFixed(2)}
                </p>
                <p className="text-xs text-gray-400">
                  Q{price.toFixed(2)} c/u
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between pt-3 border-t">
        <div>
          <span className="text-sm text-gray-500">{cart.itemCount} productos</span>
        </div>
        <div className="text-right">
          <span className="text-sm text-gray-500">Total:</span>
          <span className="ml-2 text-lg font-bold text-gray-900">Q{cart.total.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}
